import { alpha, useTheme } from "@mui/material";
import { ACCENT } from "../accent";

// Geometry is unit-circle (see frames.js); everything here just scales it.
const R = 66; // arm length in viewBox units
const PAD = 19; // rotor pad radius

/** Fuselage/wing outlines. Purely decorative — they carry no data. */
const Body = ({ kind, stroke, fill }) => {
  if (kind === "vtol") {
    return (
      <g stroke={stroke} fill={fill} strokeWidth={1.5}>
        <path d="M0,-86 C7,-72 9,-40 9,0 L9,62 L-9,62 L-9,0 C-9,-40 -7,-72 0,-86 Z" />
        <path d="M-88,-6 L88,-6 L74,12 L-74,12 Z" />
        <path d="M-30,56 L30,56 L24,70 L-24,70 Z" />
      </g>
    );
  }
  if (kind === "wing") {
    return (
      <g stroke={stroke} fill={fill} strokeWidth={1.5}>
        <path d="M0,-80 L92,44 L92,64 L-92,64 L-92,44 Z" />
      </g>
    );
  }
  return <circle r={17} stroke={stroke} fill={fill} strokeWidth={1.5} />;
};

/** Spin arrow: a near-full arc around the pad, arrowhead showing direction. */
const SpinArc = ({ spin, color }) => {
  if (!spin) return null;
  const r = PAD + 6;
  const sweep = spin === "cw" ? 1 : 0;
  const tip = spin === "cw" ? `${r - 3.5},${-r + 6.5}` : `${-r + 3.5},${-r + 6.5}`;
  return (
    <g stroke={color} fill="none" strokeWidth={1.4} opacity={0.75}>
      <path d={`M0,${-r} A${r},${r} 0 1 ${sweep} ${spin === "cw" ? -0.1 : 0.1},${-r}`} />
      <path d={`M0,${-r} L${tip}`} strokeWidth={1.6} />
    </g>
  );
};

/**
 * Top-down airframe with a pad per motor. Presentational only: it renders the
 * values it is handed and owns no interaction, so the readout list beside it
 * stays the single place motor numbers are read.
 */
const VehicleDiagram = ({ frame, rotors = {}, surfaces = {}, primaryUnit }) => {
  const theme = useTheme();
  const line = alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.28 : 0.24);
  const bodyFill = alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.05 : 0.03);
  const muted = theme.palette.text.secondary;

  const toneColor = (tone) =>
    tone === "high"
      ? theme.palette.warning.main
      : tone === "low"
        ? theme.palette.info?.main || ACCENT
        : ACCENT;

  // Coaxial arms carry two motors at the same angle: outer ring is the top
  // motor, inner disc the one beneath it.
  const arms = [];
  for (const r of frame.rotors) {
    if (r.forward) continue;
    const arm = arms.find((a) => a.deg === r.deg);
    if (arm) arm.motors.push(r);
    else arms.push({ deg: r.deg, x: r.x, y: r.y, motors: [r] });
  }
  const forward = frame.rotors.filter((r) => r.forward);

  return (
    <svg
      viewBox="-108 -108 216 216"
      width="100%"
      style={{ display: "block", maxHeight: 340 }}
      role="img"
      aria-label={`${frame.label} motor layout`}
    >
      {/* nose marker */}
      <path d="M0,-97 L5,-88 L-5,-88 Z" fill={muted} opacity={0.8} />

      {arms.map((a) => (
        <line
          key={a.deg}
          x1={0}
          y1={0}
          x2={a.x * R}
          y2={a.y * R}
          stroke={line}
          strokeWidth={4}
          strokeLinecap="round"
        />
      ))}

      <Body kind={frame.kind} stroke={line} fill={bodyFill} />

      {arms.map((a) => {
        const [top, bottom] = a.motors;
        const ts = rotors[top.id] || {};
        const bs = bottom ? rotors[bottom.id] || {} : null;
        return (
          <g key={a.deg} transform={`translate(${a.x * R} ${a.y * R})`}>
            <SpinArc spin={top.spin} color={muted} />
            <circle
              r={PAD}
              fill={alpha(toneColor(ts.tone), 0.16 + 0.5 * (ts.load ?? 0))}
              stroke={toneColor(ts.tone)}
              strokeWidth={bs ? 3 : 1.6}
            />
            {bs && (
              <circle
                r={PAD - 6.5}
                fill={alpha(toneColor(bs.tone), 0.18 + 0.5 * (bs.load ?? 0))}
                stroke={toneColor(bs.tone)}
                strokeWidth={1.4}
              />
            )}
            <text
              y={bs ? -1.5 : 1}
              textAnchor="middle"
              fontSize={bs ? 7.5 : 9}
              fontWeight={700}
              fill={theme.palette.text.primary}
            >
              {ts.primary ?? "—"}
            </text>
            {bs ? (
              <text y={7} textAnchor="middle" fontSize={7.5} fontWeight={700} fill={theme.palette.text.primary}>
                {bs.primary ?? "—"}
              </text>
            ) : (
              <text y={9.5} textAnchor="middle" fontSize={6} fill={muted}>
                {primaryUnit}
              </text>
            )}
            <text
              y={a.y < 0 ? -PAD - 9 : PAD + 15}
              textAnchor="middle"
              fontSize={7.5}
              fontWeight={600}
              fill={muted}
            >
              {a.motors.map((m) => m.id).join(" / ")}
            </text>
          </g>
        );
      })}

      {forward.map((r) => {
        const s = rotors[r.id] || {};
        return (
          <g key={r.id} transform={`translate(0 ${frame.kind === "wing" ? 74 : 80})`}>
            <rect
              x={-24}
              y={-11}
              width={48}
              height={22}
              rx={5}
              fill={alpha(toneColor(s.tone), 0.16 + 0.5 * (s.load ?? 0))}
              stroke={toneColor(s.tone)}
              strokeWidth={1.6}
            />
            <text y={2.5} textAnchor="middle" fontSize={8.5} fontWeight={700} fill={theme.palette.text.primary}>
              {s.primary ?? "—"}
            </text>
            <text y={20} textAnchor="middle" fontSize={7.5} fontWeight={600} fill={muted}>
              {r.id}
            </text>
          </g>
        );
      })}

      {(frame.surfaces || []).map((sf) => {
        const s = surfaces[sf.id] || {};
        return (
          <g key={sf.id} transform={`translate(${sf.x * 86} ${sf.y * 68})`}>
            <rect x={-19} y={-8} width={38} height={16} rx={3} fill={bodyFill} stroke={line} strokeWidth={1.2} />
            <text y={2.5} textAnchor="middle" fontSize={7} fill={theme.palette.text.primary}>
              {s.pwm ?? "—"}
            </text>
            <text y={-11.5} textAnchor="middle" fontSize={6.5} fill={muted}>
              {sf.id}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default VehicleDiagram;
